import { memo, useRef, useMemo, useCallback, useState, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Crud, CrudMode, notification, rejectionError, transformCrudFormErrors, getSelectsOptions } from '@ohpen/enso-ui';
import type { CrudFormComponent, CrudProps, FormValues, RenderFormItem, SelectsOptions, ValidationError } from '@ohpen/enso-ui';
import {
  ChangeRequestOfExclusivitySetRequest,
  ExclusivitySetRequest,
  ExclusivitySetResponse,
  ExclusivitySetsCrudChangeResponse,
  FieldType,
  FirmGetAllResponse,
  productExclusivitySetsCreateChangeRequest,
} from '@pmp/api';
import { crudToActionMap, changeRequestNotificationMessage, changeRequestButtonMessage } from '@pmp/utils';
import { CrudFormProps } from '@pmp/types';
import { useFeatureToggle } from '@pmp/hooks';
import { TagListPanel } from '@pmp/components';

import { getFormSchema } from '../formConfig';
import { buildPrincipalIdToFirmIds } from '../utils';

type FormShape = ExclusivitySetRequest & { principal?: string | null };

type Props = CrudFormProps<ExclusivitySetResponse> & {
  firms?: FirmGetAllResponse[];
};

export const ExclusiveProductsCrudForm = memo(
  ({ mode, record, valueLists, firms, refreshTable, onClose }: Props): ReactElement | null => {
    const ref = useRef<CrudFormComponent>(null);
    const [open, setOpen] = useState(true);
    const { t } = useTranslation();
    const enableFourEyes = useFeatureToggle(['enableFourEyes']);

    const handleOnClose = useCallback(() => {
      setOpen(false);
      setTimeout(() => {
        onClose();
      }, 300);
    }, [onClose]);

    const principalIdToFirmIds = useMemo(
      () => buildPrincipalIdToFirmIds(valueLists, firms),
      [valueLists, firms],
    );

    const onValuesChange = useCallback(
      (changed: FormValues) => {
        if (!('principal' in changed)) return;
        const form = ref.current?.form;
        if (!form) return;
        const principalId = (changed.principal as string | undefined | null) ?? null;
        const firmIdsForPrincipal = principalId ? principalIdToFirmIds[principalId] ?? [] : [];
        // Clear previous firm selection and pre-select all firms in the new principal's network
        form.setFieldsValue({ firms: firmIdsForPrincipal });
      },
      [principalIdToFirmIds],
    );

    const renderSelectedFirms: RenderFormItem<Record<string, unknown>> = useCallback(
      (_schema, _config, form) => {
        const selectedIds = (form.getFieldValue(['firms']) as string[] | undefined) ?? [];
        const firmNames = valueLists?.[FieldType.FIRMS];
        const items = selectedIds
          .map(id => ({ key: id, label: firmNames?.[id] ?? id }))
          .sort((a, b) => a.label.localeCompare(b.label));
        const removeFirm = (id: string): void => {
          const current = (form.getFieldValue(['firms']) as string[] | undefined) ?? [];
          form.setFieldsValue({ firms: current.filter(firmId => firmId !== id) });
        };
        return (
          <TagListPanel
            items={items}
            header={t('pmp.exclusiveProducts.labels.selectedFirmsCount', { count: items.length })}
            emptyText={t('pmp.exclusiveProducts.labels.noSelectedFirms')}
            onRemove={mode === CrudMode.VIEW ? undefined : removeFirm}
          />
        );
      },
      [valueLists, t, mode],
    );

    const onConfirm: (formData: FormShape) => Promise<ValidationError[] | void> = useCallback(
      data => {
        if (!mode || !crudToActionMap[mode]) return Promise.reject(rejectionError('mode is required'));

        const { principal, ...payload } = data; // eslint-disable-line @typescript-eslint/no-unused-vars
        const body: ChangeRequestOfExclusivitySetRequest = {
          data: payload,
          actionType: crudToActionMap[mode],
        };
        return productExclusivitySetsCreateChangeRequest({ body }).then(
          ({ data: { isValid, errors } }: { data: ExclusivitySetsCrudChangeResponse }) => {
            if (!isValid) {
              const form = ref.current?.form;
              if (form) {
                const errorFields = form.getFieldsError().map(f => ({ name: f.name, errors: [] }));
                if (errorFields.length) form.setFields(errorFields);
              }

              return transformCrudFormErrors(errors);
            }
            if (mode === CrudMode.EDIT || !enableFourEyes) refreshTable();
            notification().success({
              title: changeRequestNotificationMessage(t, enableFourEyes),
            });
          },
        );
      },
      [mode, refreshTable, t, enableFourEyes],
    );

    const title = useMemo(() => {
      if (!mode) return '';
      const titles: { [key: string]: string } = {
        [CrudMode.VIEW]: t('global.labels.viewX', { x: t('pmp.exclusiveProducts.labels.exclusiveProducts') }),
        [CrudMode.CREATE]: t('global.labels.createX', { x: t('pmp.exclusiveProducts.labels.exclusiveProducts') }),
        [CrudMode.EDIT]: t('global.labels.editX', { x: t('pmp.exclusiveProducts.labels.exclusiveProducts') }),
      };
      return titles[mode];
    }, [mode, t]);

    const initialValues: FormShape | null = useMemo(() => {
      if (!record) return null;
      const { lenderFirms, ...props } = record;
      return {
        ...props,
        firms: lenderFirms?.map(firm => firm.key) || [],
        principal: null,
      };
    }, [record]);

    const crudProps: CrudProps<FormShape> = useMemo(() => {
      const baseSelects: SelectsOptions = (valueLists && getSelectsOptions(valueLists)) || {};
      const firmsOptions = baseSelects[FieldType.FIRMS] ?? [];
      const principalsOptions = baseSelects[FieldType.PRINCIPALS] ?? [];

      return {
        ref,
        open,
        schema: getFormSchema(firmsOptions, principalsOptions, renderSelectedFirms, mode),
        title,
        labels: {
          confirm: mode === CrudMode.EDIT ? t('global.labels.save') : changeRequestButtonMessage(t, enableFourEyes),
        },
        initialValues,
        mode,
        onClose: handleOnClose,
        onConfirm,
        onValuesChange,
      };
    }, [valueLists, renderSelectedFirms, open, mode, title, t, initialValues, handleOnClose, onConfirm, onValuesChange, enableFourEyes]);

    return <Crud {...crudProps} />;
  },
);
